/* ──────────────────────────────────────────────────────────────────────
 * FlagSameEventDialog — premium "Flag as same event" picker.
 *
 * Replaces the legacy numeric "Primary lead ID" textbox + plain related-
 * leads list. Uses the searchable autocomplete pattern + backend-scored
 * candidate feed from MergeLeadDialog, paired with the chip-grid REASON
 * selector + purple CTA styling from CustomerRelationshipPanel's inline
 * dialog. Reuses the existing /leads/{id}/merge-candidates endpoint —
 * same scoring (phone / email / customer / event / name / recency).
 *
 * Business contract is UNCHANGED:
 *   • Calls onConfirm(primaryId, compiledReason)
 *   • Parent runs flagLeadSameEvent() which archives the source lead as
 *     resolution_status='same_event_duplicate'. No data is moved, history
 *     is preserved, the lead can be restored.
 * ──────────────────────────────────────────────────────────────────── */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Stack, TextField, Typography,
} from "@mui/material";
import LinkIcon from "@mui/icons-material/Link";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  fetchMergeCandidates,
  type MergeCandidate, type MergeCandidateReason, type LeadThread,
} from "./leadApi";
import {
  leadConfig, showsVenueField, isMergeReasonAllowed, resolveInquirySubject,
  duplicateWorkflowLabel,
} from "./leadVertical";
import LeadNumberBadge from "./LeadNumberBadge";

/* ─── Palette shared with the relationship panel ─────────────────── */
const TOKEN = {
  navy:        "#16233A",
  related:     "#6d28d9",
  relatedDeep: "#5b21b6",
  relatedBg:   "#ede9fe",
  relatedEdge: "#ddd6fe",
  border:      "#e5e7eb",
};

/* ─── Match-reason badges — palette per the spec ──────────────────── */
const REASON_LABEL: Record<MergeCandidateReason, string> = {
  same_phone:      "Same phone",
  same_email:      "Same email",
  same_customer:   "Same customer",
  similar_name:    "Similar name",
  same_event_type: "Same event",
  recent:          "Recent",
};

const REASON_STYLES: Record<MergeCandidateReason, { fg: string; bg: string; edge: string }> = {
  same_phone:      { fg: "#be185d", bg: "#fce7f3", edge: "#fbcfe8" }, // pink
  same_email:      { fg: "#1d4ed8", bg: "#dbeafe", edge: "#bfdbfe" }, // blue
  same_customer:   { fg: "#6d28d9", bg: "#ede9fe", edge: "#ddd6fe" }, // purple
  similar_name:    { fg: "#15803d", bg: "#dcfce7", edge: "#bbf7d0" }, // green
  same_event_type: { fg: "#b45309", bg: "#fef3c7", edge: "#fde68a" }, // orange
  recent:          { fg: "#475569", bg: "#f1f5f9", edge: "#e2e8f0" }, // gray
};

/* ─── Predefined reason chips (matches the inline-panel dialog) ─── */
const SAME_EVENT_REASONS = [
  { value: "Duplicate Web Inquiry", color: TOKEN.related },
  { value: "Quote Comparison",      color: "#0891b2"     },
  { value: "Family Inquiry",        color: "#0d9488"     },
  { value: "Internal Duplicate",    color: "#94a3b8"     },
  { value: "Spam/Test",             color: "#dc2626"     },
  { value: "Other",                 color: TOKEN.navy    },
];

interface Props {
  open: boolean;
  /** The lead being flagged. */
  sourceLead: LeadThread;
  onClose: () => void;
  /** Fires with (primaryId, compiledReason) once the operator confirms. */
  onConfirm: (primaryId: number, reason: string) => Promise<void>;
}

export default function FlagSameEventDialog({ open, sourceLead, onClose, onConfirm }: Props) {
  const [input, setInput]       = useState("");
  const [options, setOptions]   = useState<MergeCandidate[]>([]);
  const [loading, setLoading]   = useState(false);
  const [selected, setSelected] = useState<MergeCandidate | null>(null);

  const [reason, setReason] = useState<string>("Duplicate Web Inquiry");
  const [notes, setNotes]   = useState<string>("");

  const [submitting, setSubmitting] = useState(false);

  const abortRef = useRef<AbortController | null>(null);
  const initialLoadedRef = useRef(false);

  /* ── Reset every aspect of state on close so re-open starts clean. */
  useEffect(() => {
    if (!open) {
      setInput("");
      setOptions([]);
      setSelected(null);
      setReason("Duplicate Web Inquiry");
      setNotes("");
      setSubmitting(false);
      initialLoadedRef.current = false;
      abortRef.current?.abort();
      return;
    }
    if (initialLoadedRef.current) return;
    initialLoadedRef.current = true;

    let cancelled = false;
    setLoading(true);
    const controller = new AbortController();
    abortRef.current = controller;
    fetchMergeCandidates(sourceLead.id, "", controller.signal)
      .then((rows) => {
        if (cancelled || controller.signal.aborted) return;
        setOptions(rows);
        // Smart prefill — only when the strongest signal is conclusive.
        // Phone / email / customer matches all qualify; "recent" alone
        // never auto-selects (it would be too easy to flag the wrong lead).
        const top = rows[0];
        const strong = ["same_phone", "same_email", "same_customer"] as MergeCandidateReason[];
        if (top && top.match_reasons.some((r) => strong.includes(r))) {
          setSelected(top);
        }
      })
      .catch(() => { /* silent — dropdown stays empty, operator can search */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; controller.abort(); };
  }, [open, sourceLead.id]);

  /* ── Debounced typed search (350ms) ────────────────────────────── */
  useEffect(() => {
    if (!open || !initialLoadedRef.current) return;

    const trimmed = input.trim();
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    const timer = window.setTimeout(async () => {
      try {
        const rows = await fetchMergeCandidates(sourceLead.id, trimmed, controller.signal);
        if (controller.signal.aborted) return;
        setOptions(rows);
      } catch (e: any) {
        if (controller.signal.aborted || e?.name === "AbortError") return;
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, 350);
    return () => { window.clearTimeout(timer); controller.abort(); };
  }, [input, open, sourceLead.id]);

  /* ── Smart match summary — what's matching across the auto-suggest pool */
  const smartSummary = useMemo(() => {
    const seen = new Set<MergeCandidateReason>();
    for (const o of options) for (const r of o.match_reasons) seen.add(r);
    const ordered: MergeCandidateReason[] = [
      "same_phone", "same_email", "same_customer", "similar_name", "same_event_type", "recent",
    ];
    return ordered.filter((r) => seen.has(r));
  }, [options]);

  const canSubmit = !!selected && !!reason && !submitting;

  const handleConfirm = useCallback(async () => {
    if (!selected || !reason) return;
    setSubmitting(true);
    try {
      const compiled = notes ? `${reason} — ${notes}` : reason;
      await onConfirm(selected.id, compiled);
    } finally {
      setSubmitting(false);
    }
  }, [selected, reason, notes, onConfirm]);

  const sourceLabel = sourceLead.lead_number ?? `#${sourceLead.id}`;

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ fontWeight: 700, color: TOKEN.navy, display: "flex", alignItems: "center", gap: 1 }}>
        <LinkIcon sx={{ color: TOKEN.related }} />
        Flag as {duplicateWorkflowLabel.toLowerCase()}
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: 13 }}>
          Lead <b>{sourceLabel}</b> will be hidden from the active pipeline as a
          {leadConfig.supportsEventFields
            ? <> <i>same-event duplicate</i> of the primary you pick below.</>
            : <> <i>duplicate</i> of the primary you pick below.</>}
          {" "}History is preserved; the lead can be restored at any time.
        </Typography>

        {/* ── Smart match summary banner ─────────────────────────── */}
        {smartSummary.length > 0 && (
          <Box sx={{
            display: "flex", gap: 1, alignItems: "flex-start",
            p: 1.2, mb: 2, borderRadius: 1.5,
            bgcolor: TOKEN.relatedBg, border: `1px solid ${TOKEN.relatedEdge}`,
          }}>
            <InfoOutlinedIcon sx={{ fontSize: 16, color: TOKEN.relatedDeep, mt: "2px" }} />
            <Box>
              <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: TOKEN.relatedDeep, letterSpacing: 0.4, textTransform: "uppercase", mb: 0.4 }}>
                Potential duplicate leads found
              </Typography>
              <Stack direction="row" gap={0.5} flexWrap="wrap">
                {smartSummary.map((r) => {
                  const t = REASON_STYLES[r];
                  return (
                    <Box key={r} sx={{
                      fontSize: 10, fontWeight: 800, letterSpacing: 0.4,
                      color: t.fg, bgcolor: t.bg, border: `1px solid ${t.edge}`,
                      borderRadius: 0.6, px: 0.6, py: 0.1, textTransform: "uppercase",
                    }}>
                      {REASON_LABEL[r]}
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          </Box>
        )}

        {/* ── Searchable Primary picker ──────────────────────────── */}
        <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: TOKEN.related, letterSpacing: 0.5, textTransform: "uppercase", mb: 0.6 }}>
          Primary lead
        </Typography>

        <Autocomplete<MergeCandidate, false, false, false>
          options={options}
          value={selected}
          onChange={(_, v) => setSelected(v)}
          inputValue={input}
          onInputChange={(_, v, r) => { if (r !== "reset") setInput(v); }}
          loading={loading}
          filterOptions={(x) => x} // server-side
          isOptionEqualToValue={(o, v) => o.id === v.id}
          getOptionLabel={(o) => `${o.lead_number ?? `#${o.id}`} · ${o.name}`}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <CandidateRow candidate={option} />
            </li>
          )}
          noOptionsText={loading ? "Searching…" : (
            <Box sx={{ py: 1.5, textAlign: "center" }}>
              <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: TOKEN.navy }}>
                No similar leads found.
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: "#64748b", mt: 0.3 }}>
                Search by lead #, name, phone, or email.
              </Typography>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              placeholder="Search lead #, name, phone, email, event type…"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading && <CircularProgress size={16} sx={{ mr: 1 }} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          ListboxProps={{ sx: { maxHeight: 360 } }}
        />

        {/* ── Selected preview ────────────────────────────────────── */}
        {selected && (
          <Box sx={{
            mt: 1.5, p: 1.4, borderRadius: 1.5,
            bgcolor: "#fafafa", border: `1px solid ${TOKEN.border}`,
            borderLeft: `3px solid ${TOKEN.related}`,
          }}>
            <Typography sx={{ fontSize: 9.5, fontWeight: 800, color: TOKEN.related, letterSpacing: 0.6, textTransform: "uppercase", mb: 0.4 }}>
              Selected primary
            </Typography>
            <CandidateRow candidate={selected} />
          </Box>
        )}

        {/* ── Reason chip grid ────────────────────────────────────── */}
        <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: TOKEN.related, letterSpacing: 0.5, textTransform: "uppercase", display: "block", mt: 2.5, mb: 0.8 }}>
          Reason
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(3, 1fr)" }, gap: 0.7, mb: 2 }}>
          {SAME_EVENT_REASONS.map((r) => {
            const active = reason === r.value;
            return (
              <Box
                key={r.value}
                onClick={() => setReason(r.value)}
                sx={{
                  cursor: "pointer", userSelect: "none",
                  p: 0.9, borderRadius: 1.2,
                  bgcolor: active ? `${r.color}14` : "#fff",
                  border: `1.5px solid ${active ? r.color : TOKEN.border}`,
                  color: active ? r.color : "#475569",
                  transition: "all 0.15s",
                  "&:hover": { borderColor: r.color, bgcolor: `${r.color}0A` },
                }}
              >
                <Typography sx={{ fontWeight: 700, fontSize: 11.5, lineHeight: 1.3 }}>{r.value}</Typography>
              </Box>
            );
          })}
        </Box>

        <TextField
          size="small"
          fullWidth
          multiline
          rows={2}
          label="Additional notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={submitting} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!canSubmit}
          onClick={handleConfirm}
          startIcon={submitting ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <LinkIcon />}
          sx={{
            textTransform: "none", fontWeight: 700,
            bgcolor: TOKEN.related,
            "&:hover": { bgcolor: TOKEN.relatedDeep },
            "&.Mui-disabled": { bgcolor: "#cbd5e1", color: "#fff" },
          }}
        >
          {submitting ? "Flagging…" : `Flag ${duplicateWorkflowLabel}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ═════ Row renderer reused inside the Autocomplete + selected preview */

function CandidateRow({ candidate }: { candidate: MergeCandidate }) {
  const created = candidate.created_at
    ? new Date(candidate.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : "—";
  const eventDate = candidate.event_date
    ? new Date(candidate.event_date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : null;

  // Strong reasons first so the most informative badge sits leftmost.
  // Vertical-inappropriate reasons (e.g. same_event_type on autism) are
  // filtered out — they must not surface even if the API returned them.
  const sortedReasons = [...candidate.match_reasons]
    .filter((r) => isMergeReasonAllowed(r))
    .sort((a, b) => {
      const order: Record<MergeCandidateReason, number> = {
        same_phone: 0, same_email: 1, same_customer: 2,
        similar_name: 3, same_event_type: 4, recent: 5,
      };
      return order[a] - order[b];
    });

  return (
    <Box sx={{ width: "100%", py: 0.4 }}>
      <Stack direction="row" alignItems="center" gap={0.8} flexWrap="wrap" sx={{ mb: 0.3 }}>
        <LeadNumberBadge value={candidate.lead_number ?? `#${candidate.id}`} variant="muted" />
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: TOKEN.navy, textTransform: "capitalize" }}>
          {candidate.name}
        </Typography>
        <Box flex={1} />
        {sortedReasons.map((r) => {
          const t = REASON_STYLES[r];
          return (
            <Box key={r} sx={{
              fontSize: 9.5, fontWeight: 800, letterSpacing: 0.4,
              color: t.fg, bgcolor: t.bg, border: `1px solid ${t.edge}`,
              borderRadius: 0.6, px: 0.55, py: 0.1, textTransform: "uppercase", whiteSpace: "nowrap",
            }}>
              {REASON_LABEL[r]}
            </Box>
          );
        })}
      </Stack>
      <Stack direction="row" gap={1.5} flexWrap="wrap" sx={{ color: "#475569", fontSize: 11.5 }}>
        {candidate.phone && <Typography variant="caption" sx={{ fontSize: 11.5 }}>📞 {candidate.phone}</Typography>}
        {candidate.email && <Typography variant="caption" sx={{ fontSize: 11.5 }}>✉ {candidate.email}</Typography>}
        {(() => {
          if (leadConfig.supportsEventFields) {
            return candidate.event_type ? (
              <Typography variant="caption" sx={{ fontSize: 11.5, textTransform: "capitalize" }}>
                🎯 {candidate.event_type.replace(/_/g, " ")}
                {showsVenueField("event_date") && eventDate ? ` · ${eventDate}` : ""}
              </Typography>
            ) : null;
          }
          const s = resolveInquirySubject(candidate as unknown as any);
          return s.program ? (
            <Typography variant="caption" sx={{ fontSize: 11.5, textTransform: "capitalize" }}>
              🎯 {s.program}
            </Typography>
          ) : null;
        })()}
        <Typography variant="caption" sx={{ fontSize: 11.5, textTransform: "capitalize" }}>
          Status: {candidate.status}
        </Typography>
        <Typography variant="caption" sx={{ fontSize: 11.5 }}>
          Created: {created}
        </Typography>
      </Stack>
    </Box>
  );
}
